import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal, Button, Form as BootstrapForm, Row, Col } from "react-bootstrap";
import AxiosController from "../authentication/AxiosController";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { notifyError, notifySuccess } from "../toastify/toastify";

const CreateAndUpdateCategoryOffer = ({
  method,
  offerData,
  categories,
  show,
  handleModalClose,
  handleModalSuccess,
}) => {
  const { slug, lang } = useParams();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const { t: _ } = useTranslation("global");
  const box_head_dir = lang === "en" ? "ltr" : "rtl";

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required(_("panel.nameRequired")),
    category_id: Yup.mixed()
      .required(_("panel.categoryRequired"))
      .test('is-valid-category', _("panel.categoryRequired"), (value) => value && value !== ""),
    offer_type: Yup.string()
      .required(_("panel.offerTypeRequired"))
      .oneOf(["fixed_price", "percentage", "fixed_discount"]),
    offer_value: Yup.number()
      .required(_("panel.offerValueRequired"))
      .min(0, _("panel.offerValueMin")),
    responsibility: Yup.string()
      .required(_("panel.responsibilityRequired"))
      .oneOf(["system", "seller"]),
    valid_from: Yup.string().required(_("panel.validFromRequired")),
    valid_until: Yup.string().required(_("panel.validUntilRequired")),
    is_active: Yup.boolean(),
  });

  // Format datetime for input
  const formatDateTimeForInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initial form values
  const initialValues = {
    name: offerData?.name || "",
    category_id: offerData?.category_id || "",
    offer_type: offerData?.offer_type || "percentage",
    offer_value: offerData?.offer_value || "",
    responsibility: offerData?.responsibility || "system",
    valid_from: formatDateTimeForInput(offerData?.valid_from) || "",
    valid_until: formatDateTimeForInput(offerData?.valid_until) || "",
    is_active: offerData?.is_active !== undefined ? Boolean(offerData.is_active) : true,
  };

  // Form submission handler
  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    
    // Format dates for API
    const formattedValues = {
      ...values,
      is_active: values.is_active ? 1 : 0,
      valid_from: values.valid_from.replace("T", " "),
      valid_until: values.valid_until.replace("T", " ").split("T")[0] || values.valid_until.replace("T", " "),
    };

    const apiUrl =
      method === "edit"
        ? `/api/${slug}/${lang}/category-offers/${offerData.id}`
        : `/api/${slug}/${lang}/category-offers`;

    const requestMethod =
      method === "edit" ? AxiosController.put : AxiosController.post;

    requestMethod(apiUrl, formattedValues, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setLoading(false);
        resetForm();
        notifySuccess(
          method === "edit"
            ? _("panel.updateSuccessMsg")
            : _("panel.successMsg")
        );
        if (handleModalSuccess) handleModalSuccess();
      })
      .catch((error) => {
        setLoading(false);
        if (error.response && typeof error.response.data === "object") {
          const errorMessages = Object.entries(error.response.data)
            .map(
              ([key, value]) =>
                `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
            )
            .join(", ");
          notifyError(errorMessages);
        } else {
          notifyError(
            Array.isArray(error.response?.data)
              ? error.response.data.join(", ")
              : error.response?.data || _("panel.errorOccurred")
          );
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title dir={box_head_dir}>
          {method === "edit" ? _("panel.editCategoryOffer") : _("panel.addCategoryOffer")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body dir={box_head_dir}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Label>{_("panel.offerName")}</BootstrapForm.Label>
                    <Field
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder={_("panel.enterOfferName")}
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger small"
                    />
                  </BootstrapForm.Group>
                </Col>
                <Col md={6}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Label>{_("panel.category")}</BootstrapForm.Label>
                    <Field as="select" name="category_id" className="form-select">
                      <option value="">{_("panel.selectCategory")}</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.arabic_name || cat.english_name || cat.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="category_id"
                      component="div"
                      className="text-danger small"
                    />
                  </BootstrapForm.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Label>{_("panel.offerType")}</BootstrapForm.Label>
                    <Field as="select" name="offer_type" className="form-select">
                      <option value="percentage">{_("panel.percentage")}</option>
                      <option value="fixed_price">{_("panel.fixedPrice")}</option>
                      <option value="fixed_discount">{_("panel.fixedDiscount")}</option>
                    </Field>
                    <ErrorMessage
                      name="offer_type"
                      component="div"
                      className="text-danger small"
                    />
                  </BootstrapForm.Group>
                </Col>
                <Col md={6}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Label>{_("panel.offerValue")}</BootstrapForm.Label>
                    <Field
                      type="number"
                      name="offer_value"
                      className="form-control"
                      placeholder={
                        values.offer_type === "percentage"
                          ? _("panel.enterPercentage")
                          : _("panel.enterValue")
                      }
                      min="0"
                      step="0.01"
                    />
                    <ErrorMessage
                      name="offer_value"
                      component="div"
                      className="text-danger small"
                    />
                  </BootstrapForm.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Label>{_("panel.responsibility")}</BootstrapForm.Label>
                    <Field as="select" name="responsibility" className="form-select">
                      <option value="system">{_("panel.system")}</option>
                      <option value="seller">{_("panel.seller")}</option>
                    </Field>
                    <ErrorMessage
                      name="responsibility"
                      component="div"
                      className="text-danger small"
                    />
                  </BootstrapForm.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Label>{_("panel.validFrom")}</BootstrapForm.Label>
                    <Field
                      type="datetime-local"
                      name="valid_from"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="valid_from"
                      component="div"
                      className="text-danger small"
                    />
                  </BootstrapForm.Group>
                </Col>
                <Col md={6}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Label>{_("panel.validUntil")}</BootstrapForm.Label>
                    <Field
                      type="datetime-local"
                      name="valid_until"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="valid_until"
                      component="div"
                      className="text-danger small"
                    />
                  </BootstrapForm.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <BootstrapForm.Group>
                    <BootstrapForm.Check
                      type="switch"
                      id="is_active"
                      label={_("panel.isActive")}
                      checked={values.is_active === true || values.is_active === 1}
                      onChange={(e) =>
                        setFieldValue("is_active", e.target.checked)
                      }
                    />
                  </BootstrapForm.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                >
                  {_("panel.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || loading}
                  style={{
                    backgroundColor: "rgb(89, 198, 201)",
                    border: "none",
                  }}
                >
                  {isSubmitting || loading
                    ? _("panel.saving")
                    : method === "edit"
                    ? _("panel.update")
                    : _("panel.save")}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default CreateAndUpdateCategoryOffer;
